import { BoundValue, ChildRef, Scope } from "./types";
import { summarizeValue } from "./format";

/**
 * Path resolution for A2UI BoundValues.
 *
 * Absolute paths (start with /): looked up in the data model with
 * longest-prefix-match, then dotted-traversal of the rest.
 *
 * Relative paths (no leading /): looked up against the current scope
 * (a template-row item, when iterating a List).
 */

export function resolveBound(
  bv: BoundValue | undefined,
  dataModel: Record<string, unknown>,
  scope: Scope,
): unknown {
  if (bv == null) return null;
  // Order matters — literal*  wins over path if both present (catalog
  // requires exactly one, but be defensive).
  const obj = bv as Record<string, unknown>;
  if ("literalString" in obj  && obj.literalString  !== undefined) return obj.literalString;
  if ("literalBoolean" in obj && obj.literalBoolean !== undefined) return obj.literalBoolean;
  if ("literalNumber" in obj  && obj.literalNumber  !== undefined) return obj.literalNumber;
  if ("literalList" in obj    && obj.literalList    !== undefined) return obj.literalList;
  if ("path" in obj && typeof obj.path === "string") {
    return readPath(obj.path, dataModel, scope);
  }
  return null;
}

export function readPath(
  path: string,
  dataModel: Record<string, unknown>,
  scope: Scope,
): unknown {
  if (!path.startsWith("/")) {
    // Relative path — read from scope.
    return readObjectPath(scope, path);
  }
  // Direct hit
  if (path in dataModel) return dataModel[path];
  // Longest-prefix match into a parent key
  let prefix = path;
  while (true) {
    const i = prefix.lastIndexOf("/");
    if (i <= 0) return null;
    prefix = prefix.slice(0, i);
    if (prefix in dataModel) {
      return readObjectPath(dataModel[prefix], path.slice(prefix.length + 1));
    }
  }
}

function readObjectPath(obj: unknown, relPath: string): unknown {
  if (obj == null) return null;
  const parts = relPath.split("/").filter(Boolean);
  let cur: unknown = obj;
  for (const p of parts) {
    if (cur == null || typeof cur !== "object") return null;
    cur = (cur as Record<string, unknown>)[p];
  }
  return cur;
}

/** Resolve a child reference (entryPointChild, contentChild, child, or template componentId). */
export function resolveChildRef(ref: ChildRef | string | undefined): string | null {
  if (typeof ref === "string") return ref;
  if (!ref || typeof ref !== "object") return null;
  if (typeof ref.literalString === "string") return ref.literalString;
  if (typeof ref.componentId === "string") return ref.componentId;
  return null;
}

/** Coerce arbitrary values to a display string — NEVER raw JSON.
 *  Objects/arrays become human one-line summaries (see format.ts);
 *  components that can afford rich layout use <NestedValue> instead. */
export function formatText(v: unknown): string {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return summarizeValue(v);
}
